import { useEffect, useRef, useState } from 'react';
import { Info, X } from 'lucide-react';

const TOOLTIP_LABEL = 'Used to accept forwarded emails';

export interface RequiredSenderBadgeProps {
  /**
   * Override the accessible label on the badge button. Useful so the
   * surrounding row reads naturally for screen readers
   * (e.g. "user@example.com — Required, used to accept forwarded emails").
   */
  ariaLabel?: string;
}

/**
 * The forwarding address row in Approved Senders is system-managed: RLS
 * blocks DELETE on it, and removing it would break Gmail's redirect to
 * the Yaruta label. We surface that contract to the user with a tappable
 * "Required" badge whose tooltip explains *why* — replacing the trash
 * icon a regular row would have shown. Used in both the YAR-238
 * walkthrough Edit Senders sub-state and Settings → Approved Senders.
 */
export function RequiredSenderBadge({ ariaLabel }: RequiredSenderBadgeProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Click-outside + Escape both dismiss the tooltip. Re-bind listeners
  // only while the tooltip is actually open so other rows' clicks aren't
  // intercepted unnecessarily.
  useEffect(() => {
    if (!open) return;
    const handlePointerDown = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  return (
    <div
      ref={containerRef}
      className="relative shrink-0"
    >
      <button
        type="button"
        onClick={(e) => {
          // Stop the click from bubbling to the row (some rows are
          // pressable, e.g. Settings list items).
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        aria-expanded={open}
        aria-label={
          ariaLabel ?? `Required sender — ${TOOLTIP_LABEL.toLowerCase()}`
        }
        className="inline-flex items-center gap-1 rounded-full border border-border bg-background px-2 py-0.5 text-sm font-medium leading-5 text-muted-foreground cursor-pointer"
      >
        Required
        <Info
          size={14}
          className="text-icon-default"
        />
      </button>
      {open && (
        <div
          role="tooltip"
          className="absolute right-0 top-full mt-2 z-[300] w-[200px] rounded-lg border border-border bg-surface-muted px-3 py-2 shadow-md"
        >
          <div className="flex items-start gap-2">
            <p className="flex-1 text-sm leading-5 text-foreground">
              {TOOLTIP_LABEL}
            </p>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setOpen(false);
              }}
              aria-label="Dismiss tooltip"
              className="shrink-0 cursor-pointer text-icon-default"
            >
              <X size={14} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
